import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import FrontHeader from "../../shared/FrontHeader";
import FrontFooter from "../../shared/FrontFooter";
import { setTitle } from "../../helpers/MetaTag";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setLanguage } from "../../redux/slice/languageSlice";
import { eventDetails } from "../../services/eventService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { organizationDetails } from "../../services/organizationService";
import LoadingFallback from "../../components/elements/LoadingFallback";
import Input from "../../components/form/Input";
import Modal from "../../components/elements/Modal";
import Button from "../../components/form/Button";
import { toast } from "react-toastify";
import useFindRoute from "../../hooks/useFindRoute";
import { langConfig } from "../../config/LangConfig";
import { apiRequest } from "../../util/apiUtils";
import { referVerificationDetails } from "../../services/ticketService";

function ConfirmPassword({ checkPasswordHandler = () => {}, isPending }) {
  const [modalOpen, setModalOpen] = useState(true);
  const [password, setPassword] = useState("");

  return (
    <Modal isOpen={modalOpen} size="md" title={"Password"} closeBtn={false}>
      <div className="space-y-4">
        <div className="text-lg font-medium text-deep-purple">
          Please enter password to view this event
        </div>
        <Input
          inputType="password"
          inputPlaceholder={"Enter password"}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          value={password}
          onKeyDown={(e) => {
            if (password && e.code === "Enter") {
              checkPasswordHandler(password).then((resp) => {
                if (resp?.res?.status === 200) {
                  setModalOpen(false);
                }
              });
            }
          }}
        />
        <Button
          buttonClasses={"w-full"}
          buttonLabel={"Submit"}
          buttonFunction={() => {
            if (password) {
              checkPasswordHandler(password).then((resp) => {
                if (resp?.res?.status === 200) {
                  setModalOpen(false);
                }
              });
            }
          }}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Modal>
  );
}

const FrontLayout = ({ metaTitle }) => {
  setTitle(`stackora | ${metaTitle}`);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const { matchedRoute } = useFindRoute();
  const navigate = useNavigate();
  const { lang: paramsLang } = useParams();
  const { lang } = useSelector((state) => state?.langProvider);
  const { eventId, orgId } = useParams();
  const savePassword = eventId
    ? sessionStorage.getItem(`${eventId}-password`)
    : false;

  useEffect(() => {
    if (langConfig?.includes(paramsLang)) {
      if (paramsLang !== lang) {
        dispatch(setLanguage(paramsLang));
        localStorage.setItem("lang", paramsLang);
      }
    } else {
      const save_lang = localStorage.getItem("lang");
      let uri_sagement = location?.pathname?.split("/");
      uri_sagement[1] = save_lang || "en";
      navigate(uri_sagement.join("/"));
    }
  }, [paramsLang, lang]);

  const {
    data: eventDetailsData,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["event-details", eventId, location?.key],
    queryFn: async () => {
      if (
        eventDetailsData?.routerLocationKey &&
        eventDetailsData?.routerLocationKey !== location?.key
      ) {
        queryClient.removeQueries({
          queryKey: ["event-details"],
          exact: false,
        });
      }
      const res = await eventDetails({ id: eventId, type: "front" });
      return { ...res, routerLocationKey: location?.key };
    },
    enabled: !!eventId,
  });

  const {
    data: organizerDetailsData,
    isLoading: organizerLoading,
    isFetching: organizerFetching,
    refetch: organizationRefetch,
  } = useQuery({
    queryKey: [
      "event-organizer",
      eventDetailsData?.data?.organizationData?._id || orgId,
    ],
    queryFn: async () => {
      const res = await organizationDetails({
        ...(orgId
          ? { id: orgId, type: "front" }
          : {
              id: eventDetailsData?.data?.organizationData?._id,
            }),
      });

      if (res?.status === 200) {
        return {
          ...res,
          data: {
            ...res?.data,
            themeSetting: {
              ...res?.data?.themeSetting,
              styles: Object.assign(
                ...Object.keys(res?.data?.themeSetting?.styles || {})?.map(
                  (key) => ({
                    [key]: Object.assign(
                      ...res?.data?.themeSetting?.styles[key]?.map((item) => ({
                        [item?.key]: item?.value,
                      })),
                      {}
                    ),
                  })
                ),
                {}
              ),
            },
          },
        };
      } else {
        toast.warning(res?.message);
        return {};
      }
    },
    enabled: !!eventDetailsData?.data?.organizationData?._id || !!orgId,
  });

  const { data: promoterDetails, isLoading: promoterDetailsIsLoading } =
    useQuery({
      enabled: !!queryObject?.referral_code || !!location?.state?.promoterUid,
      queryKey: [
        "promoter-details",
        queryObject?.referral_code || "",
        location?.state?.promoterUid || "",
        location,
      ],
      queryFn: async () => {
        const res = await referVerificationDetails({
          code: queryObject?.referral_code || location?.state?.promoterUid,
          type: "front",
        });
        if (!res?.data?.verified) {
          if (location?.state?.promoterUid) {
            const state = {
              ...location?.state,
              ...(location?.state?.checkoutData && {
                checkoutData: location?.state?.checkoutData?.map((item) => {
                  return {
                    ...item,
                    price: item?.originalPrice,
                    totalPrice: item?.buy_quentity * item?.originalPrice,
                  };
                }),
              }),
            };
            delete state?.promoterUid;

            navigate(location?.pathname, { state: state });
          }
          if (queryObject?.referral_code) {
            navigate(location?.pathname);
          }
        }
        return res;
      },
    });

  const {
    mutateAsync: checkPasswordHandler,
    isPending,
    data,
  } = useMutation({
    mutationFn: async (value) => {
      const res = await eventDetails({
        id: eventId,
        type: "front",
        password: value,
      });

      return { res, pass: value, passwordCheck: res?.status === 200 };
    },
    onSuccess: ({ res, pass }) => {
      if (res?.status === 200) {
        sessionStorage.setItem(`${eventId}-password`, pass);
      } else {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.endsWith(`${eventId}-password`)) {
            sessionStorage.removeItem(key);
            // Adjust index after removal
            i--;
          }
        }
        toast.warning("Password not matched. Try again!");
      }
    },
  });

  useEffect(() => {
    if (savePassword) {
      checkPasswordHandler(savePassword);
    }
  }, [savePassword]);

  if (eventId && (isLoading || isPending)) {
    return <LoadingFallback />;
  } else if (
    eventDetailsData?.data &&
    eventDetailsData?.data?.visibility === "private" &&
    eventDetailsData?.data?.hasPassword &&
    !data?.passwordCheck
  ) {
    return (
      <ConfirmPassword
        checkPasswordHandler={checkPasswordHandler}
        isPending={isPending}
      />
    );
  }

  return (
    <>
      <FrontHeader
        {...(matchedRoute === "/:lang/organization/details/:orgId"
          ? {
              organizer: {
                data: organizerDetailsData,
                loading: organizerLoading || organizerFetching,
                selectedLanguage: lang,
              },
            }
          : {})}
      />
      <Outlet
        context={
          eventId
            ? {
                event: eventDetailsData,
                eventLoading: isLoading || isFetching,
                selectedLanguage: lang,
                eventOrganizer: {
                  organizationRefetch,
                  data: organizerDetailsData?.data,
                  loading:
                    isLoading || isFetching
                      ? true
                      : organizerLoading || organizerFetching,
                },
                eventPromoter: {
                  data: promoterDetails,
                  loading: promoterDetailsIsLoading,
                },
              }
            : {
                organizer: organizerDetailsData,
                loading: organizerLoading || organizerFetching,
                selectedLanguage: lang,
                organizationRefetch,
              }
        }
      />
      <FrontFooter />
    </>
  );
};

export default FrontLayout;
